import { Canvas, Edge, EdgeData, ElkRoot, NodeData, PortData } from "reaflow";
import { CustomLabel, CustomNode } from "../components/flow/components/graphs";
import { TaskStatus } from "../types";
import { getFlowTheme } from "../components/flow/theme";

import { MouseEvent, useRef } from "react";

const dashedEdgeStyles = {
  stroke: "#b1b1b7",
  strokeDasharray: "5",
  strokeDashoffset: 10,
  strokeWidth: 1,
  markerEnd: "none",
};

interface CustomNodeActions {
  toggleNodeMenu: (node: NodeData) => void;
}

interface WorkflowVisualizerNodesAndEdgesProps {
  nodes: NodeData[];
  edges: EdgeData[];
  onClick?: (e: MouseEvent<HTMLElement>, data: NodeData) => void;
  handleLayoutChange: (value: ElkRoot) => void;
  onToggleTaskMenu?: (event: MouseEvent<HTMLElement>, port: PortData) => void;
  onDeleteBranch?: (
    __event: any,
    {
      port,
      node,
    }: {
      port: PortData;
      node: NodeData;
    }
  ) => void;
  isInconsistent?: boolean;
  customNodeActions?: CustomNodeActions;
  selectEdge?: ({ edge }: { edge: EdgeData }) => void;
  CustomNod?: React.ReactElement;
}

function WorkflowVisualizerNodesAndEdges({
  nodes,
  edges,
  onClick,
  handleLayoutChange,
  onToggleTaskMenu,
  onDeleteBranch,
  isInconsistent = false,
  customNodeActions,
  selectEdge,
  CustomNod,
}: // onClick,
WorkflowVisualizerNodesAndEdgesProps) {
  const canvasRef = useRef(null);
  const theme = getFlowTheme("light");

  const CustomNodeRenderer = CustomNod ?? (
    <CustomNode
      onClick={onClick}
      actions={customNodeActions ? customNodeActions : null}
      onToggleTaskMenu={onToggleTaskMenu ? onToggleTaskMenu : () => null}
      onDeleteBranch={onDeleteBranch ? onDeleteBranch : () => null}
      isInconsistent={isInconsistent}
    />
  );

  return (
    <Canvas
      ref={canvasRef}
      pannable={false}
      zoomable={false}
      animated={true}
      fit={false}
      nodes={nodes}
      edges={edges}
      layoutOptions={{
        "org.eclipse.elk.spacing.edgeEdge": "170",
        "org.eclipse.elk.padding": "[top=10,left=100,bottom=10,right=100]",
        "org.eclipse.elk.layered.edgeLabels.centerLabelPlacementStrategy":
          "SPACE_EFFICIENT_LAYER",
      }}
      edge={(edge: EdgeData) => {
        const edgeStylesForTaskStatus = [
          TaskStatus.COMPLETED,
          TaskStatus.COMPLETED_WITH_ERRORS,
        ].includes(edge?.data?.status)
          ? {
              stroke: theme.edges.completed.stroke,
              strokeWidth: theme.edges.completed.strokeWidth,
            }
          : {
              stroke: theme.edges.default.stroke,
              strokeWidth: theme.edges.default.strokeWidth,
            };
        const edgeStyles =
          edge?.data?.unreachableEdge === true
            ? dashedEdgeStyles
            : edgeStylesForTaskStatus;
        return (
          <Edge
            selectable={false}
            label={
              <CustomLabel
                nodes={nodes}
                selectEdge={selectEdge ? selectEdge : () => null}
              />
            }
            style={edgeStyles}
          />
        );
      }}
      node={() => CustomNodeRenderer}
      onLayoutChange={handleLayoutChange}
    />
  );
}

export default WorkflowVisualizerNodesAndEdges;
